import React, { useState, useEffect } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Typography, Alert, Chip, TablePagination
} from '@mui/material';
import { leaveRequestService, leaveBalanceService } from '../services/api';
import { format } from 'date-fns';
import { usePagination } from '../hooks/usePagination';
import { RequestLeaveDialog } from './shared/RequestLeaveDialog';

const LeaveManagement: React.FC = () => {
  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [balance, setBalance] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadLeaveRequests();
    loadBalance();
  }, []);

  const loadLeaveRequests = async () => {
    try {
      const data = await leaveRequestService.getMyLeaveRequests();
      setLeaveRequests(data);
    } catch (error) {
      console.error('Error loading leave requests:', error);
    }
  };

  const loadBalance = async () => {
    try {
      const data = await leaveBalanceService.getMyBalance();
      setBalance(data);
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  const handleSubmitted = (msg: string) => {
    setMessage(msg);
    loadLeaveRequests();
    loadBalance();
    setTimeout(() => setMessage(''), 3000);
  };

  const handleCancel = async (id: number) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      const res = await leaveRequestService.cancelLeaveRequest(id);
      setMessage(res?.message || 'Leave request cancelled.');
      loadLeaveRequests();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error cancelling leave request');
    }
  };

  const { pagedRows: pagedLeaveRequests, paginationProps: leaveRequestsPaginationProps } = usePagination(leaveRequests);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'error';
      case 'Cancelled': return 'default';
      default: return 'warning';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Leave Requests</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>
          Request Leave
        </Button>
      </Box>

      {balance && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
          <Typography variant="subtitle1" gutterBottom>Leave Balance</Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Chip label={`Available: ${balance.totalBalance} day(s)`} color="info" />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Sick Leave and Earned Leave share this same balance — half day leave counts as 0.5.
          </Typography>
          {(balance.pendingSickLeave > 0 || balance.pendingEarnedLeave > 0) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Balance above already accounts for leave awaiting approval
              {balance.pendingSickLeave > 0 && ` (Sick: ${balance.pendingSickLeave} pending`}
              {balance.pendingSickLeave > 0 && balance.pendingEarnedLeave > 0 && ', '}
              {balance.pendingEarnedLeave > 0 && `Earned: ${balance.pendingEarnedLeave} pending`}
              {(balance.pendingSickLeave > 0 || balance.pendingEarnedLeave > 0) && ')'}
            </Typography>
          )}
        </Paper>
      )}

      {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Leave Type</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested On</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedLeaveRequests.map((leave) => (
              <TableRow key={leave.id}>
                <TableCell>{format(new Date(leave.startDate), 'MMM dd, yyyy')}</TableCell>
                <TableCell>{format(new Date(leave.endDate), 'MMM dd, yyyy')}</TableCell>
                <TableCell>
                  {leave.leaveType}
                  {leave.dayType === 'Half' && (
                    <Chip label="Half Day" size="small" variant="outlined" sx={{ ml: 1 }} />
                  )}
                  {leave.isUnpaid && leave.leaveType !== 'Leave Without Pay' && (
                    <Chip label="Unpaid" color="warning" size="small" sx={{ ml: 1 }} />
                  )}
                  {(() => {
                    // Partial Paid/LWP split (e.g. 4 days requested with only 2 days of balance).
                    const total = leave.dayType === 'Half'
                      ? 0.5
                      : Math.round((new Date(leave.endDate).getTime() - new Date(leave.startDate).getTime()) / 86400000) + 1;
                    const paid = leave.paidDays ?? total;
                    return !leave.isUnpaid && paid < total ? (
                      <Chip label={`${paid} Paid + ${Math.round((total - paid) * 100) / 100} LWP`} color="warning" size="small" sx={{ ml: 1 }} />
                    ) : null;
                  })()}
                </TableCell>
                <TableCell>{leave.reason}</TableCell>
                <TableCell>
                  <Chip label={leave.status} color={getStatusColor(leave.status)} size="small" />
                </TableCell>
                <TableCell>{format(new Date(leave.requestedAt), 'MMM dd, yyyy')}</TableCell>
                <TableCell>
                  {leave.status === 'Pending' && (
                    <Button size="small" color="error" variant="outlined" onClick={() => handleCancel(leave.id)}>
                      Cancel
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {leaveRequests.length > 0 && <TablePagination {...leaveRequestsPaginationProps} />}
      </TableContainer>

      <RequestLeaveDialog
        open={open}
        onClose={() => setOpen(false)}
        onSubmitted={handleSubmitted}
        existingLeaveRequests={leaveRequests}
      />
    </Box>
  );
};

export default LeaveManagement;
